import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React from 'react';
import { FaArrowRight, FaFileAlt, FaFileInvoice, FaReceipt, FaCertificate } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from 'components/app/DrawerContext';

type ReportCardType = {
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  path: string;
  isActive: boolean;
  category: string;
  visibility: 'admin' | 'investor' | 'all';
};

const FundManagementReports: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fundManagementAdminRight } = useDrawer();

  // Single master array for all reports
  const allReportCards: ReportCardType[] = [
    {
      title: 'My Certificates',
      description: 'View and download your investment unit certificates and holdings',
      icon: <FaCertificate size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      path: '/fundManagementInvestment/reports/certificates',
      isActive: true,
      category: 'Documents',
      visibility: 'all'
    },
    {
      title: 'Expense Ledger',
      description: 'View detailed expense transactions and ledger entries for all units',
      icon: <FaFileInvoice size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/fundManagementInvestment/reports/expense-ledger',
      isActive: true,
      category: 'Financial',
      visibility: 'admin'
    },
    {
      title: 'Investor Ledger',
      description: 'View your personal ledger transactions across all groups and units',
      icon: <FaReceipt size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)',
      path: '/fundManagementInvestment/reports/investor-ledger',
      isActive: true,
      category: 'Financial',
      visibility: 'investor'
    }
  ];

  // Decide what to show
  const visibleCards = allReportCards.filter(
    (card) =>
      card.visibility === 'all' ||
      (fundManagementAdminRight ? card.visibility === 'admin' : card.visibility === 'investor')
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        padding: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)'
      }}
    >
      {/* Header */}
      <Box sx={{ marginBottom: { xs: 2, md: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
          <Box
            sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: { xs: 1, md: 1.5 }
            }}
          >
            <FaFileAlt size={isMobile ? 14 : 16} color="white" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 500,
                color: '#333',
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
              }}
            >
              Fund Management Reports
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
              }}
            >
              {fundManagementAdminRight
                ? 'Generate and access fund administration reports'
                : 'Access your investment documents and statements'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Report Cards Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        {visibleCards.map((report, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card
              sx={{
                height: { xs: 140, sm: 160, md: 180 },
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                boxShadow: '0 3px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              {!report.isActive && (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: { xs: '0.55rem', sm: '0.6rem' },
                    height: { xs: 16, sm: 18 },
                    backgroundColor: 'rgba(255, 99, 71, 0.15)',
                    color: '#d32f2f',
                    border: '1px solid rgba(211, 47, 47, 0.2)',
                    fontWeight: 500,
                    zIndex: 1
                  }}
                />
              )}

              <CardActionArea
                onClick={() => {
                  if (report.isActive) {
                    navigate(report.path);
                  }
                }}
                disabled={!report.isActive}
                sx={{ height: '100%' }}
              >
                <CardContent
                  sx={{
                    padding: { xs: 1.5, sm: 2 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        width: { xs: 36, sm: 42, md: 48 },
                        height: { xs: 36, sm: 42, md: 48 },
                        borderRadius: 1.5,
                        background: report.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: { xs: 1, sm: 1.5 },
                        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      {report.icon}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        marginBottom: { xs: 0.5, sm: 0.8 },
                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                        lineHeight: 1.2,
                        color: '#333'
                      }}
                    >
                      {report.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        lineHeight: 1.4,
                        fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {report.description}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: { xs: 1, sm: 1.5 }
                    }}
                  >
                    <Chip
                      label={report.category}
                      size="small"
                      sx={{
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        height: { xs: 16, sm: 18 },
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        color: '#667eea',
                        fontWeight: 500
                      }}
                    />
                    {report.isActive && (
                      <Button
                        size="small"
                        endIcon={<FaArrowRight size={8} />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: { xs: '0.6rem', sm: '0.65rem' },
                          padding: { xs: '2px 8px', sm: '4px 10px' },
                          minHeight: { xs: 22, sm: 26 }
                        }}
                      >
                        View
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default FundManagementReports;
